import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { LogCategory, LogDirective, LoggerService, LogType } from '../core/services/logger.service';
import { ProfileService } from '../core/services/profile.service';
import { UnavailableComponent } from '../core/unavailable/unavailable.component';
import { IWidgetComponent } from '../shared/widget/i-widget-component';
import { Widget, WidgetMeta, ContentWidget } from '../shared/widget/widget';
import { AnnouncementsComponent } from '../widgets/announcements/announcements.component';
//import { BiddingComponent } from '../widgets/bidding/bidding.component';
import { CalendarComponent } from '../widgets/calendar/calendar.component';
import { CanvasAssignmentsComponent } from '../widgets/canvas-assignments/canvas-assignments.component';
import { ContactsComponent } from '../widgets/contacts/contacts.component';
//import { DropAddComponent } from '../widgets/drop-add/drop-add.component';
import { EventsComponent } from '../widgets/careers/events/events.component';
import { FacultyCoursesComponent } from '../widgets/faculty-courses/faculty-courses.component';
import { JobPostingsComponent } from '../widgets/careers/job-postings/job-postings.component';
import { MyCoursesComponent } from '../widgets/my-courses/my-courses.component';
import { PointsTrackingComponent } from '../widgets/points-tracking/points-tracking.component';
import { CmsComponent } from '../widgets/content/cms.component';
import { KudosComponent } from '../widgets/kudos/kudos.component';
import { MySupportRequestsComponent } from '../widgets/my-support-requests/my-support-requests.component';
import { TransferCreditsComponent } from '../widgets/transfer-credits/transfer-credits.component';
import { AgendaComponent } from '../widgets/agenda/agenda.component';
import { MyDegreeComponent } from '../widgets/my-degree/my-degree.component';
import { MyLinksComponent } from '../widgets/my-links/my-links.component';
import { MyDocumentsComponent } from '../widgets/my-documents/my-documents.component';
import { MyFavoritesComponent } from '../widgets/my-favorites/my-favorites.component';
import { TilePageComponent } from '../widgets/tile-page/tile-page.component';
import { MyPagesComponent } from '../widgets/my-pages/my-pages.component';

@Directive({
    selector: '[appWidgetsHost]',
    standalone: false
})
export class WidgetsHostDirective implements OnInit, OnDestroy {

    // There's a good article on Dynamic Component Loading here: https://angular.io/guide/dynamic-component-loader 

    @Input() widgetList: string;
    @Input() moduleName: string;
    @Input() isModal: boolean = false;
    @Output() onWidgetsLoaded = new EventEmitter<Widget[]>();

    private _profileSubscription: Subscription;
    private widgets: Widget[] = [];
    //Teams Intergration 2021
    private isChromeless: boolean = false;

    constructor(
        public viewContainerRef: ViewContainerRef,
        private profileService: ProfileService,        
        private logger: LoggerService,
    ) {
        this.log("constructor", "", LogCategory.Constructor);
        //console.log("widget host DIRECTIVE constructor");
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Directive, LogDirective.WidgetsHost, category);
    }

    ngOnInit() {
        this.log("ngOnInit");
        //console.log("widget host DIRECTIVE oninit");
        this._profileSubscription = this.profileService.profile$.subscribe(data => {
            this.log("ngOnInit", "profile$ fetched", LogCategory.SubscriptionData);
            if (data) {
                // We're reinitializing the page, so clear the list of Widgets. In some cases, profile$ was
                // emmitting twice. Each time it emmitted, it would add the same widgets again (first
                // time adding 2, and the second time adding 2+2 more, for a total of 6). It shouldn't be
                // emmitting twice in the first place, but in case it does, this will at least remove
                // one of the three sets of duplicates (the +2)
                this.widgets = [];

                // Get the widgets and preferences that are availble to the user per the database. These widgets are 
                // shared across all pages. 
                const metaStore = this.profileService.widgetStore;

                // Get the list of widgets that the user wants displayed on this page, in the order they should be
                // displayed, or grab an empty array if the user has not set this preference. Add to the end of this 
                // list any widgets that the component says should appear on the page, in the order provided by
                // the component.
                let widgetsOnPage: string[] = [];// new array of widget  
                widgetsOnPage.push(...(this.profileService.widgetPlacements[this.moduleName] || [])); // add the user placements first
                widgetsOnPage.push(...this.widgetList.split(","));                  // append component-defined widgets
                widgetsOnPage = widgetsOnPage.filter(item => item.trim() != '');    // filter out blanks
                widgetsOnPage = Array.from(new Set(widgetsOnPage));               // unique ordered list
                this.log("ngOnInit", "Widget order for page: " + widgetsOnPage);

                //Teams Integration 2021
                if (this.moduleName === "Chromeless")
                    this.isChromeless = true;

                // If this is a dashboard page, only show favorited widgets, but use the order provided by widgetsOnPage for 
                // any favorited widgets that are found in that list. If they're not in that list, then add them to the end.
                //  For MyKE V2 we aren't taking into account the IsFave setting on the dashboard anymore.
                if ((this.moduleName === "DashboardComponent") && (data.version != 2)) {
                    // Go trough the widgetsOnPage to define the order of the favorites
                    let addedToPage = {};
                    widgetsOnPage.forEach((value) => {
                        if (!!metaStore[value] && metaStore[value].IsFave) {
                            this.widgets.push(this.createWidget(value, metaStore[value], this.profileService.widgetBetas.includes(value)));
                            addedToPage[value] = true;
                        }
                    });

                    // Go through the store of metas to see if there were any favorites we missed that weren't include in the page 
                    Object.keys(metaStore).forEach((value) => {
                        if (!!metaStore[value] && metaStore[value].IsFave && !addedToPage[value]) {
                            this.widgets.push(this.createWidget(value, metaStore[value], this.profileService.widgetBetas.includes(value)));
                        }
                    });
                }
                else {  // otherwise it's not a dashboard page, so add a widget with the shared meta for each item on the list.
                    for (let i = 0; i < widgetsOnPage.length; i++) {
                        const widgetName: string = widgetsOnPage[i];
                        if (!!metaStore[widgetName]) {   // If we have widget meta for it, then continue 
                            //Teams Integeration 2021
                            this.widgets.push(this.createWidget(widgetName, metaStore[widgetName], this.profileService.widgetBetas.includes(widgetName), this.isChromeless));
                        }
                    }
                }

                this.log("ngOnInit", `Added to page '${this.moduleName}': ` + this.widgets.map(a => a.meta.Name));

                this.loadWidgets();
            }
        });
    }

    ngOnDestroy() {
        this.log("ngOnDestroy");
        if (!!this._profileSubscription) {
            this.log("ngOnDestroy", "Unsubscribe _profileSubscription");
            this._profileSubscription.unsubscribe();
        }
    }

    private loadWidgets() {
        this.log("loadWidgets");
        this.viewContainerRef.clear();
        this.widgets.forEach((widgetItem) => {            
            const componentRef = this.viewContainerRef.createComponent(widgetItem.component);
            (<IWidgetComponent>componentRef.instance).data = widgetItem;
        });

        this.onWidgetsLoaded.emit( this.widgets );
    }
    
    private createWidget(widgetName: string, meta: WidgetMeta, isBeta: boolean, isChromeless: boolean = this.isChromeless, isModal: boolean = this.isModal): Widget {
        this.log("createWidget", `Module ${this.moduleName}; Widget ${widgetName}`);
        if (isBeta)
            meta.IsBeta = true;
        //Teams Hackathon 2021
        if (isChromeless)
            meta.IsChromeless = true;
        if (isModal)
            meta.IsModal = true;
        switch (widgetName) {
            case "MyCourses":
                return new Widget(this.moduleName, MyCoursesComponent, meta);
            case "JobPostings":
                return new Widget(this.moduleName, JobPostingsComponent, meta);
            case "Events":
                return new Widget(this.moduleName, EventsComponent, meta);
            case "PointsTracking":
                return new Widget(this.moduleName, PointsTrackingComponent, meta);
            case "Announcements":
                return new Widget(this.moduleName, AnnouncementsComponent, meta);
            case "Contacts":
                return new Widget(this.moduleName, ContactsComponent, meta);
            case "Documents":
                return new ContentWidget("document", this.moduleName, CmsComponent, meta);
            case "Links":
                return new ContentWidget("link", this.moduleName, CmsComponent, meta);           
            case "Calendar":
                return new Widget(this.moduleName, CalendarComponent, meta);
            case "CanvasAssignments":
                return new Widget(this.moduleName, CanvasAssignmentsComponent, meta);
            case "Forms":
                return new ContentWidget("form", this.moduleName, CmsComponent, meta);          
            case "FacultyCourses":
                return new Widget(this.moduleName, FacultyCoursesComponent, meta);
            case "FacultyInformation":
                return new ContentWidget("instructional-suppor", this.moduleName, CmsComponent, meta);
            case "Kudos":
                return new Widget(this.moduleName, KudosComponent, meta);
            case "MySupportRequests":
                return new Widget(this.moduleName, MySupportRequestsComponent, meta);
            case "TransferCredits":
                return new Widget(this.moduleName, TransferCreditsComponent, meta);
            case "Agenda":
                return new Widget(this.moduleName, AgendaComponent, meta);
            case "MyDegree":
                return new Widget(this.moduleName, MyDegreeComponent, meta);
            case "Finance":
                return new ContentWidget("finance", this.moduleName, CmsComponent, meta);
            case "Crises":
                return new ContentWidget("crisis", this.moduleName, CmsComponent, meta);
            case "MyLinks":
                return new Widget(this.moduleName, MyLinksComponent, meta);
            case "MyDocuments":
                return new Widget(this.moduleName, MyDocumentsComponent, meta);
            case "MyFavorites":
                return new Widget(this.moduleName, MyFavoritesComponent, meta);
            case "TilePage":
                return new Widget(this.moduleName, TilePageComponent, meta);
            case "MyPages":
                return new Widget(this.moduleName, MyPagesComponent, meta);
            default:
                return new Widget(this.moduleName, UnavailableComponent, meta);
        }
    }
}
